import i18n from 'live/services/i18n'
import perfI18n from '../../../../i18n/performance-i18n'
import { Card, LineEvent, LineState, Mode } from '../types'
import Utils from 'live/lib/utils'

export const type = 'performance-overview'
export const name = i18n('Rig and Well Performance Overview')
export const description = i18n('Rig and Well Performance Overview Description')

export const sortOptions = [
    { label: perfI18n('RCI (Consistency)'), value: perfI18n('RCI (Consistency)') },
    { label: perfI18n('RPI (Performance)'), value: perfI18n('RPI (Performance)') },
    { label: perfI18n('Potential savings'), value: perfI18n('Potential savings') },
    { label: i18n('Rig name'), value: i18n('Rig name') }
]

interface SortOption {
    label: string
    value: string
}

export const sortRigs = (cards: Card[], option: SortOption): Card[] => {
    const sorted = [...cards]

    switch (option.value) {
        case perfI18n('RCI (Consistency)'):
            sorted.sort((a, b) => {
                const aVal = a.header?.consistency ?? 0
                const bVal = b.header?.consistency ?? 0

                return bVal - aVal
            })
            break

        case perfI18n('RPI (Performance)'):
            sorted.sort((a, b) => {
                const aVal = a.header?.rpi ?? 0
                const bVal = b.header?.rpi ?? 0

                return bVal - aVal
            })
            break

        case perfI18n('Potential savings'):
            sorted.sort((a, b) => {
                const aSavings =
                    a.operationRows?.reduce((acc, row) => acc + (row?.potentialSavingsOverGoal?.value ?? 0), 0) ?? 0
                const bSavings =
                    b.operationRows?.reduce((acc, row) => acc + (row?.potentialSavingsOverGoal?.value ?? 0), 0) ?? 0

                return bSavings - aSavings
            })
            break

        case i18n('Rig name'):
        default:
            sorted.sort((a, b) => a.rigName.localeCompare(b.rigName))

            sorted.sort((a, b) => {
                const aStartsWith = a.rigName.toUpperCase().startsWith('NS') || a.rigName.toUpperCase().startsWith('SS')
                const bStartsWith = b.rigName.toUpperCase().startsWith('NS') || b.rigName.toUpperCase().startsWith('SS')
                if (aStartsWith && !bStartsWith) return -1
                if (!aStartsWith && bStartsWith) return 1

                return 0
            })
            break
    }

    return sorted
}

const pxToGridY = (y: number): number => (y + 10) / 20

export const resizeY = (
    dashboardContext: LiveDashboardContextValue,
    mode: Mode,
    id: string | number,
    y: number
): void => {
    if (!dashboardContext) {
        return
    }
    const dashboardCFG = { ...dashboardContext }
    const shallowCFG: LiveDashboardContextValue = { ...dashboardCFG }
    const { actions, dashboard } = shallowCFG

    if (dashboard?.jsonConfig && dashboard?.jsonConfig?.layoutModes) {
        dashboard.jsonConfig.layoutModes[mode].grid[String(id)].size_y = pxToGridY(y)
        dashboard.jsonConfig.layoutModes[mode].gridRowHeight = 'static'
        const {
            jsonConfig: { layoutModes }
        } = dashboard
        actions.updateUserLayout(layoutModes[mode])
    }
}

export function getPointFormattedValue(point: LiveChartPoint): string {
    return typeof point.formattedValue === 'string' || typeof point.formattedValue === 'number'
        ? point.formattedValue
        : typeof point.y === 'number'
        ? Utils.formatNumber(point.y, 2, 2)
        : point.y
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const chartEvents = (
    state: LineState,
    chartEvent: LineEvent[] | Record<string, unknown>[] = [],
    rigName: string
) => {
    let event = {
        ...state,
        events: []
    }

    if (rigName && chartEvent[rigName]) {
        event = {
            ...event,
            events: chartEvent[rigName]
        }
    }

    return event
}

export function formatTimestampToDate(ts: string): string {
    const date = new Date(Number(ts))
    const dformat =
        [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-') +
        ' ' +
        [
            date.getHours()?.toString()?.length === 1 ? `0${date.getHours()}` : date.getHours(),
            date.getMinutes()?.toString()?.length === 1 ? `0${date.getMinutes()}` : date.getMinutes()
        ].join(':')

    return dformat
}

export function checkAndMountTimestamp(tsBegin: string, tsEnd: string): string {
    let span = 'last 24 hours'
    if (tsBegin) {
        const ts1 = `${tsBegin ?? 0}`
        const ts2 = `${tsEnd ?? 0}`

        span =
            ts1 === '0' && ts1.length === 1 && ts2 === '0' && ts2.length === 1
                ? 'last 24 hours'
                : ts1 !== '0' && ts2 === '0'
                ? `${formatTimestampToDate(ts1)} to now`
                : `${formatTimestampToDate(ts1)} to ${formatTimestampToDate(ts2)}`
    }

    return span
}

export function setDashboardTimespan(dashboardCtx: LiveDashboardContextValue, span: string): void {
    if (!dashboardCtx) {
        return
    }

    if (
        typeof dashboardCtx.actions.setTimespan === 'function' &&
        typeof dashboardCtx.getWidgetStateForIndex === 'function'
    ) {
        dashboardCtx.actions.setTimespan('from ts 0', undefined, false)
        // dashboardCtx.actions.setTimespan(span, undefined, true)
    }
}

export const setUrlParam = (key, value) => {
    const [baseUrl = '', queryString = ''] = window.location.hash.substring(1)?.split('?') ?? ['', '']
    const paramsArray = queryString.split('&')
    const params = {}
    const newParamsArray = []

    paramsArray.forEach(param => {
        const [k, v] = param.split('=')
        if (!params.hasOwnProperty(k)) {
            params[k] = decodeURIComponent(v)
        }
    })

    params[key] = encodeURIComponent(value)

    for (const k in params) {
        newParamsArray.push(`${k}=${params[k]}`)
    }

    window.location.hash = `#${baseUrl}?${newParamsArray.join('&')}`
}

export const getUrlParam = (key: string, defaultValue: unknown = '') => {
    const [, queryString = ''] = window.location.hash.substring(1)?.split('?')
    const paramsArray = queryString.split('&')
    const params = {}

    paramsArray.forEach(param => {
        if (!param) return
        const [k, ...valueParts] = param.split('=')
        // Junta novamente os valores caso tenha '=' no valor
        const value = valueParts.join('=')
        if (k) {
            params[k] = decodeURIComponent(decodeURIComponent(value))
        }
    })

    if (params.hasOwnProperty(key)) {
        try {
            return JSON.parse(params[key])
        } catch (error) {
            return params[key]
        }
    }

    return defaultValue
}

type minutesTypes = {
    days: { value: number; unit: string }
    hours: { value: number; unit: string }
    mins: { value: number; unit: string }
}

export function handleMinutes(minutes): string {
    const days = Math.floor(minutes / 1440) // One day has 1440 minutes (24 hours * 60 minutes)
    const hours = Math.floor((minutes % 1440) / 60)
    const mins = minutes % 60

    const cmins = Math.round(mins)

    const parts = []
    if (days !== 0) parts.push(`${days}d`)
    if (hours !== 0) parts.push(`${hours}h`)
    if (cmins !== 0 || parts.length === 0) parts.push(`${cmins}min`)

    return parts.join(' ')
}
